<template>
  <div class="swiper-container-wrapper">
    <div class="swiper-container">
      <div class="swiper-wrapper" :class="{ '-inactive': !isSliderActive }">
        <div class="swiper-slide" v-for="image in images" :key="image.src">
          <img :src="image.src" :alt="image.alt" />
        </div>
      </div>
    </div>
    <div v-show="isSliderActive" class="swiper-pagination-container">
      <div class="swiper-pagination" />
    </div>
    <div v-show="isSliderActive" class="swiper-button-next hiddenSp" />
    <div v-show="isSliderActive" class="swiper-button-prev hiddenSp" />
  </div>
</template>

<script lang="ts">
import {
  defineComponent,
  nextTick,
  onMounted,
  onUnmounted,
  PropType,
  ref,
  watch
} from 'vue'
import Swiper, { Navigation, Pagination } from 'swiper'
import 'swiper/swiper-bundle.css'

interface Image {
  src: string
  alt: string
}

export default defineComponent({
  props: {
    images: {
      type: Array as PropType<Image[]>,
      default: () => []
    }
  },

  setup(props) {
    Swiper.use([Navigation, Pagination])
    let mySwiper: any = null
    const isSliderActive = ref(false)
    const breakpoint = 568

    const initSlider = () => {
      mySwiper = new Swiper('.swiper-container', {
        pagination: {
          el: '.swiper-pagination',
          clickable: true
        },
        navigation: {
          nextEl: '.swiper-button-next',
          prevEl: '.swiper-button-prev'
        },
        breakpoints: {
          568: {
            slidesPerView: 2.1,
            centeredSlides: true,
            spaceBetween: 40,
            slidesOffsetBefore: 0,
            slidesOffsetAfter: 0,
            loop: true
          }
        }
      })
      isSliderActive.value = true
    }

    watch(
      () => props.images,
      () => {
        nextTick(() => {
          if (
            (props.images.length <= 1 && mySwiper) ||
            (props.images.length === 2 &&
              window.innerWidth >= breakpoint &&
              mySwiper)
          ) {
            mySwiper.destroy(false, true)
            mySwiper = null
            isSliderActive.value = false
          } else {
            if (mySwiper) {
              mySwiper.update()
            } else if (
              props.images.length >= 3 ||
              (props.images.length >= 2 && window.innerWidth < breakpoint)
            ) {
              initSlider()
            }
          }
        })
      }
    )

    const resizeUpdate = () => {
      if (
        props.images.length === 2 &&
        window.innerWidth >= breakpoint &&
        mySwiper
      ) {
        mySwiper.destroy(false, true)
        mySwiper = null
        isSliderActive.value = false
      }
      if (
        props.images.length === 2 &&
        window.innerWidth < breakpoint &&
        !mySwiper
      ) {
        initSlider()
      }
    }

    window.addEventListener('resize', resizeUpdate)
    onUnmounted(() => window.removeEventListener('resize', resizeUpdate))

    onMounted(() => {
      if (
        props.images.length <= 1 ||
        (props.images.length === 2 && window.innerWidth >= 568)
      )
        return

      initSlider()
    })

    return {
      isSliderActive
    }
  }
})
</script>

<style lang="scss" scoped>
@use 'src/styles/abstracts/mixins';
@use 'src/styles/abstracts/variables' as var;

.swiper-container-wrapper {
  position: relative;

  @include mixins.media('sm') {
    padding-left: 16px;
    padding-right: 16px;
  }
}

.swiper-wrapper {
  align-items: center;

  &.-inactive {
    justify-content: center;

    .swiper-slide {
      margin-left: 20px;
      margin-right: 20px;
      flex-shrink: 1;
      max-width: 343px;
    }
  }
}
.swiper-slide {
  width: 100%;
  height: auto;
}

.swiper-button-next,
.swiper-button-prev {
  position: absolute;
  top: 50%;
  width: 50px;
  height: 50px;
  color: var.$color-main !important;
  background-color: #fff;
  box-shadow: 0 3px 6px rgba(0, 0, 0, 0.16);
  border-radius: 6px;
  transition: 0.2s;

  &:hover {
    background-color: #f5f5f5;
  }

  &::after {
    transform: scale(0.4);
  }
}

.swiper-button-next {
  right: 0;
}
.swiper-button-prev {
  left: 0;
}

.swiper-pagination-container {
  display: flex;
  justify-content: center;
  margin-top: 16px;
  height: 12px;
}

.swiper-pagination {
  display: flex;
}

::v-deep .swiper-pagination-bullet {
  width: 12px;
  height: 12px;
  margin-left: 8px;
  margin-right: 8px;

  &-active {
    background-color: var.$color-main;
  }
}
</style>
